import React, { useEffect, useMemo } from "react";
import Select from "react-select";
import { useQuery } from "@tanstack/react-query";
import { fetchLabDepartment } from "services/dropdown";
import { DROPDOWN_STALE_TIME } from "utils/constants";
import { Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import { getLabDeptlist } from "../../../redux/action/lab_dept_action";

const LabDepartmentDropdown = ({
  errors,
  control,
  setValue,
  getValues,
  watch,
  rowData,
  required,
  disabled = false,
}) => {
  const dispatch = useDispatch();

  const departmentName =
    rowData?.lab_department_obj?.department ||
    rowData?.department ||
    getValues()?.lab_department;
  const departmentId =
    rowData?.lab_department_obj?.id ||
    rowData?.lis_investigation_sequence_data?.lab_department_id ||
    getValues()?.lab_department_id;

  const selectedLabDepartment = watch("lab_department_id");

  const {
    data: labDepartments,
    isLoading,
    error,
  } = useQuery(["Lab Departments"], () => fetchLabDepartment(), {
    staleTime: DROPDOWN_STALE_TIME,
  });

  const options = useMemo(() => {
    return (
      labDepartments?.data?.data?.map((ele) => ({
        value: ele.id,
        label: ele.department,
      })) || []
    );
  }, [labDepartments]);

  const selectedOption = useMemo(() => {
    return (
      options.find((option) => option.value === selectedLabDepartment) ||
      options.find((option) => option.value === departmentId) ||
      options.find((option) => option.label === departmentName) ||
      null
    );
  }, [options, selectedLabDepartment, departmentId, departmentName]);

  useEffect(() => {
    if (rowData && options.length) {
      const rowDataOption = options.find(
        (option) =>
          option.value === rowData?.lab_department_obj?.id ||
          option.value === rowData?.lab_department_id
      );
      if (rowDataOption) {
        setValue("lab_department_id", rowDataOption.value);
        setValue("lab_department", rowDataOption.label);
      }
    }
  }, [rowData, options, setValue]);

  useEffect(() => {
    if (selectedOption) {
      setValue("lab_department_id", selectedOption.value);
      setValue("lab_department", selectedOption.label);
    }
  }, [selectedOption, setValue]);

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading Lab Departments</div>;

  return (
    <Controller
      name="lab_department_id"
      control={control}
      rules={{
        required: required ? "Lab Department is required" : false,
        validate: (value) => {
          if (required && !value) return "Lab Department is required";
          return true;
        },
      }}
      render={({ field }) => (
        <Select
          {...field}
          isDisabled={disabled}
          options={options}
          isClearable
          value={selectedOption || null}
          placeholder="Select Lab Department"
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
          onChange={(selectedOption) => {
            const value = selectedOption ? selectedOption.value : "";
            field.onChange(value);
            setValue("lab_department_id", value);
            setValue(
              "lab_department",
              selectedOption ? selectedOption.label : ""
            );
            dispatch(getLabDeptlist(value));
          }}
          className={`react-select-container ${
            errors.lab_department_id ? "is-invalid" : ""
          }`}
        />
      )}
    />
  );
};

export default LabDepartmentDropdown;
